// The navbar (was _Navbar.cshtml): the logo and site name (a link to Home), an Admin button for admins, and the dropdown
// of links (academy.css).
// Its label comes from the route (see navbarText), its account links from the session. Logging out goes through the
// API, then to the landing page; when it fails, a toast says so and the page stays.
// The links keep the tabindex site.ts gave every link on the Razor pages.

import { useQueryClient } from "@tanstack/react-query";
import { postForm } from "@core/api";
import { showToast } from "@core/toasts";
import { navigateTo } from "@core/navigation";
import { GameBadge, Logo } from "../components/academy";
import { PopoutTooltip } from "../components/PopoutTooltip";
import { useNavCollapsed, useShortSiteName } from "./frame";
import { applySession, type Session, useSession } from "./session";

export const LOGOUT_FAILED = "Unable to log out.";

/** A dropdown link's classes (academy.css). */
const link = "ac-menu-link";

const GAMES = ["Hearts", "Spades", "Euchre"] as const;

export function Navbar({ text }: { text: string }) {
    const session = useSession();
    const collapsed = useNavCollapsed();
    const shortName = useShortSiteName();
    const queryClient = useQueryClient();

    async function logOut() {
        try {
            applySession(queryClient, await postForm<Session>("/api/account/logout"));
        }
        catch (error) {
            console.error(error);
            showToast(LOGOUT_FAILED);
            return;
        }

        navigateTo("/");
    }

    return (
        <div id="site-nav" className={collapsed ? "vertically-collapsed" : undefined}>
            <nav aria-label="Site">
                <div className="ac-nav-start">
                    <a className="ac-brand" href="/Home" tabIndex={0}>
                        <Logo />
                        <span id="cga-text">{shortName ? "CGA" : "Card Game Academy"}</span>
                    </a>
                    {session.isAdmin && <a id="admin-link" className="ac-btn ac-btn-sm" href="/Admin" tabIndex={0}>Admin</a>}
                </div>
                <PopoutTooltip
                    // One text node, "Spades ", as _Navbar's: two ("Spades", " ") measure a fraction of a pixel wider
                    header={<span className="dropdown-toggle" role="button" tabIndex={0} aria-label={`Menu: ${text}`}>{`${text} `}<span className="caret"></span></span>}
                >
                    <a className={link} href="/Home" tabIndex={0}>Home</a>
                    <a className={link} href="/Home/Help" tabIndex={0}>Help</a>
                    <h3>Games</h3>
                    {GAMES.map(game => (
                        <a key={game} className={link} href={`/Study/${game}`} tabIndex={0}><GameBadge game={game} />{game}</a>
                    ))}
                    <h3>Account</h3>
                    {session.isSignedIn
                        ? (
                            <>
                                <a className={link} href="/Account/Profile" tabIndex={0}>Profile</a>
                                <a className={link} href="/Home/ComingSoon" tabIndex={0}>Premium</a>
                                <form method="post" action="/account/logout" onSubmit={event => { event.preventDefault(); void logOut(); }}>
                                    <button className={link} type="submit">Logout</button>
                                </form>
                            </>
                        )
                        : (
                            <>
                                <a className={link} href="/Account/Login" tabIndex={0}>Login</a>
                                <a className={link} href="/Account/Register" tabIndex={0}>Register</a>
                            </>
                        )}
                </PopoutTooltip>
            </nav>
        </div>
    );
}
